import React, { useState } from "react";
import { FaBed, FaBath, FaChild, FaBroom, FaInfoCircle, FaArrowRight, FaCheck } from "react-icons/fa";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useColorMode } from "@chakra-ui/react";
import { motion } from "framer-motion";

const BaseTarifaire = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const [showDJF, setShowDJF] = useState(false);

  const fadeUp = (i = 0) => ({
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut", delay: i * 0.1 } },
  });

  const services = [
    {
      icon: FaBed,
      title: "Garde & Nuit",
      tag: "Personnes âgées",
      priceBase: "30,02",
      priceDJF: "36,16",
      pricePCH: "25,00",
      description: "Présence rassurante de jour comme de nuit pour les personnes âgées ou dépendantes.",
      items: [
        { label: "Aide au coucher et au lever", included: true },
        { label: "Surveillance et assistance nocturne", included: true },
        { label: "Majoration nuit +25%", included: true },
        { label: "PCH / APA disponible", included: true },
        { label: "Garde d'enfants", included: false },
      ],
    },
    {
      icon: FaBath,
      title: "Actes Essentiels",
      tag: "Perte d'autonomie",
      priceBase: "30,02",
      priceDJF: "36,16",
      pricePCH: "25,00",
      description: "Accompagnement quotidien des gestes essentiels par des intervenants diplômés.",
      items: [
        { label: "Aide à la toilette et à l'hygiène", included: true },
        { label: "Aide au lever et au coucher", included: true },
        { label: "Aide à l'habillage", included: true },
        { label: "PCH / APA disponible", included: true },
        { label: "Accompagnement sorties", included: true },
      ],
    },
    {
      icon: FaChild,
      title: "Garde d'Enfants",
      tag: "Famille",
      priceBase: "30,02",
      priceDJF: "36,16",
      pricePCH: null,
      description: "Prise en charge professionnelle de vos enfants à domicile, y compris en situation de handicap.",
      items: [
        { label: "Sorties et retours d'école", included: true },
        { label: "Babysitting à domicile", included: true },
        { label: "Enfants en situation de handicap", included: true },
        { label: "PCH / APA disponible", included: false },
        { label: "Aide nocturne adultes", included: false },
      ],
    },
    {
      icon: FaBroom,
      title: "Entretien & Ménage",
      tag: "Domicile",
      priceBase: "30,02",
      priceDJF: "36,16",
      pricePCH: "25,00",
      description: "Entretien régulier ou ponctuel de votre domicile, adapté à vos exigences.",
      items: [
        { label: "Entretien régulier du domicile", included: true },
        { label: "Ménage approfondi", included: true },
        { label: "Intervention ponctuelle", included: true },
        { label: "PCH / APA disponible", included: true },
        { label: "Aide à la personne", included: false },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Tarifs aide à domicile — Hetep Iaout Services</title>
        <meta name="description" content="Consultez les tarifs de nos services d'aide à domicile. Tarification transparente, crédit d'impôt de 50 % applicable. Seniors, enfants, handicap, entretien." />
        <link rel="canonical" href="https://www.hisvie.com/tarifs/" />
        <meta property="og:title" content="Tarifs aide à domicile — Hetep Iaout Services" />
      </Helmet>

      <div className={`min-h-screen pt-28 pb-24 transition-colors duration-300 ${isDark ? "bg-[#0a0f16]" : "bg-[#F0F4FF]"}`}>

        {/* ── HERO ── */}
        <motion.section
          initial="hidden" animate="visible" variants={fadeUp(0)}
          className="text-center px-6 mb-14"
        >
          <div className="inline-flex items-center gap-2 mb-6">
            <span className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase border ${isDark ? "bg-blue-900/30 border-blue-700/40 text-blue-300" : "bg-white border-blue-200 text-blue-600 shadow-sm"}`}>
              Tarifs en vigueur
            </span>
          </div>

          <h1 className={`text-5xl md:text-7xl font-black mb-5 tracking-tight leading-[1.05] ${isDark ? "text-white" : "text-[#1B365D]"}`}>
            Des tarifs <span className="text-blue-600 font-serif italic font-light">clairs.</span>
          </h1>
          <p className={`text-base md:text-lg max-w-lg mx-auto leading-relaxed font-light mb-10 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Transparence totale sur nos prestations. Un devis personnalisé vous est remis gratuitement.
          </p>

          {/* Toggle semaine / DJF */}
          <div className={`inline-flex items-center gap-1 p-1 rounded-xl border ${isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200 shadow-sm"}`}>
            <button
              onClick={() => setShowDJF(false)}
              className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${!showDJF ? isDark ? "bg-blue-600 text-white shadow-md" : "bg-[#1B365D] text-white shadow-md" : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            >
              Tarif semaine
            </button>
            <button
              onClick={() => setShowDJF(true)}
              className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${showDJF ? "bg-red-500 text-white shadow-md" : isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
            >
              Dim. & Fériés
            </button>
          </div>
        </motion.section>

        {/* ── GRILLE DES CARDS ── */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 mb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <motion.div
                key={i}
                custom={i} initial="hidden" animate="visible" variants={fadeUp(i * 0.1)}
                className={`relative flex flex-col rounded-[1.75rem] border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                  isDark
                    ? "bg-gray-900/80 border-gray-800 hover:border-gray-700"
                    : "bg-white border-gray-200 shadow-md hover:shadow-xl"
                }`}
              >

                {/* En-tête card */}
                <div className={`px-7 pt-8 pb-6 border-b ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${isDark ? "bg-gray-800" : "bg-blue-50"}`}>
                    <s.icon className={`text-base ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  </div>

                  <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {s.tag}
                  </p>
                  <h2 className={`text-xl font-black mb-2 ${isDark ? "text-white" : "text-[#1B365D]"}`}>
                    {s.title}
                  </h2>
                  <p className={`text-xs leading-relaxed font-light ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {s.description}
                  </p>
                </div>

                {/* Bloc prix */}
                <div className={`px-7 py-6 border-b ${isDark ? "border-gray-800" : "border-gray-100"}`}>
                  <div className="flex items-end gap-1 mb-1">
                    <motion.span
                      key={showDJF ? "djf" : "base"}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`text-5xl font-black tabular-nums leading-none ${
                        showDJF ? "text-red-400" : isDark ? "text-white" : "text-[#1B365D]"
                      }`}
                    >
                      {showDJF ? s.priceDJF : s.priceBase}
                    </motion.span>
                    <span className={`text-base font-bold mb-1 ${showDJF ? "text-red-400" : "text-gray-400"}`}>
                      &nbsp;€
                    </span>
                  </div>
                  <p className={`text-xs font-medium ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                    {showDJF ? "Dimanches & Jours fériés" : "par heure · tarif HIS"}
                  </p>

                  {/* PCH pill */}
                  <div className={`mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest ${
                    s.pricePCH
                      ? isDark ? "bg-gray-800 text-gray-400" : "bg-blue-50 text-blue-600"
                      : isDark ? "bg-gray-800/50 text-gray-700" : "bg-gray-100 text-gray-300"
                  }`}>
                    {s.pricePCH ? `PCH / APA : ${s.pricePCH} €` : "PCH / APA : N/A"}
                  </div>
                </div>

                {/* Features list */}
                <div className="px-7 py-6 flex-grow">
                  <ul className="space-y-3.5">
                    {s.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                          item.included
                            ? isDark ? "bg-blue-900/60 text-blue-400" : "bg-blue-100 text-blue-600"
                            : isDark ? "bg-gray-800 text-gray-700" : "bg-gray-100 text-gray-300"
                        }`}>
                          {item.included
                            ? <FaCheck size={8} />
                            : <span className="block w-2 h-0.5 bg-current rounded-full" />
                          }
                        </span>
                        <span className={`text-sm ${
                          item.included
                            ? isDark ? "text-gray-300 font-medium" : "text-gray-700 font-medium"
                            : isDark ? "text-gray-700 line-through" : "text-gray-300 line-through"
                        }`}>
                          {item.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="px-7 pb-7 pt-2">
                  <Link to="/demande-de-devis">
                    <button className={`w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all ${
                      isDark
                        ? "bg-gray-800 text-white hover:bg-gray-700 border border-gray-700"
                        : "bg-[#1B365D] text-white hover:bg-[#1e4080]"
                    }`}>
                      Demander un devis <FaArrowRight size={11} />
                    </button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── NOTE LÉGALE ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-7xl mx-auto px-4 md:px-6 mb-16"
        >
          <div className={`rounded-2xl border p-7 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6 ${isDark ? "bg-gray-900/60 border-gray-800" : "bg-white border-gray-200 shadow-sm"}`}>
            <div className={`flex items-start gap-3 pb-6 md:pb-0 border-b md:border-b-0 md:border-r md:pr-6 ${isDark ? "border-gray-800" : "border-gray-100"}`}>
              <FaInfoCircle className={`shrink-0 mt-0.5 ${isDark ? "text-blue-400" : "text-blue-600"}`} size={15} />
              <div>
                <p className={`text-xs font-black uppercase tracking-widest mb-1.5 ${isDark ? "text-white" : "text-[#1B365D]"}`}>PCH</p>
                <p className={`text-xs leading-relaxed font-light ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  HIS s'aligne sur le tarif PCH réglementaire à{" "}
                  <strong className={isDark ? "text-white" : "text-[#1B365D]"}>25&nbsp;€/h</strong>{" "}
                  pour réduire votre reste à charge.
                </p>
              </div>
            </div>
            <div className={`flex items-start gap-3 pb-6 md:pb-0 border-b md:border-b-0 md:border-r md:px-6 ${isDark ? "border-gray-800" : "border-gray-100"}`}>
              <FaInfoCircle className="shrink-0 mt-0.5 text-red-400" size={15} />
              <div>
                <p className={`text-xs font-black uppercase tracking-widest mb-1.5 ${isDark ? "text-white" : "text-[#1B365D]"}`}>APA</p>
                <p className={`text-xs leading-relaxed font-light ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Le tarif HIS s'applique aux bénéficiaires APA, pouvant entraîner un reste à charge selon votre plan d'aide.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 md:pl-6">
              <FaInfoCircle className={`shrink-0 mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`} size={15} />
              <div>
                <p className={`text-xs font-black uppercase tracking-widest mb-1.5 ${isDark ? "text-white" : "text-[#1B365D]"}`}>Nuits & DJF</p>
                <p className={`text-xs leading-relaxed font-light ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Nuits majorées de +25% sur la base HIS. Tarif DJF applicable les dimanches et jours fériés.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── CTA FINAL ── */}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-center px-6"
        >
          <p className={`text-sm font-light ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            Une situation particulière ?{" "}
            <Link to="/contact" className={`font-bold transition-colors ${isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}>
              Contactez-nous
            </Link>
            {" "}pour un accompagnement personnalisé.
          </p>
        </motion.div>

      </div>
    </>
  );
};

export default BaseTarifaire;